// app/patchSpyMiddleware.ts
import type { Middleware, AnyAction } from "@reduxjs/toolkit";
import type { Patch } from "immer";

type ApiLike = {
  reducerPath: string;
  internalActions?: {
    queryResultPatched?: { match: (a: AnyAction) => boolean };
  };
};

export const RTKQ_PATCH_EVENT = "rtkq:patch" as const;
export const RTKQ_PATCH_ERROR_EVENT = "rtkq:patchError" as const;

function countAffectedItems(patches: readonly Patch[]): number {
  const indices = new Set<number>();
  for (const p of patches) {
    if (!p?.path) continue;
    // hitta första numeriska segmentet i path (första array-index)
    const firstNum = p.path.find((seg) => typeof seg === "number") as number | undefined;
    if (typeof firstNum === "number") indices.add(firstNum);
  }
  return indices.size;
}

function countOps(patches: readonly Patch[]) {
  let add = 0,
    remove = 0,
    replace = 0;
  for (const p of patches) {
    if (!p || typeof (p as any).op !== "string") continue;
    const op = (p as any).op as string;
    if (op === "add") add++;
    else if (op === "remove") remove++;
    else if (op === "replace") replace++;
  }
  return { add, remove, replace };
}

export const makePatchSpyMiddleware =
  (slices: ApiLike[]): Middleware =>
  (store) =>
  (next) =>
  (action: AnyAction) => {
    const res = next(action);

    // ---- PATCH (apply/undo) ----
    const isPatchedAction = slices.some((api) => {
      const byMatcher = api.internalActions?.queryResultPatched?.match?.(action) === true;
      const byType = action.type === `${api.reducerPath}/queries/queryResultPatched`;
      return byMatcher || byType;
    });

    if (isPatchedAction) {
      const queryCacheKey: string = action?.payload?.queryCacheKey;
      const patches: readonly Patch[] = (action?.payload?.patches as readonly Patch[]) ?? [];
      const changed = Array.isArray(patches) && patches.length > 0;

      // Hitta vilken slice som matchar actionen
      const matchedSlice = slices.find(
        (api) =>
          api.internalActions?.queryResultPatched?.match?.(action) || action.type.startsWith(`${api.reducerPath}/`),
      );

      const state: any = store.getState();
      const sub = state?.[matchedSlice?.reducerPath]?.queries?.[queryCacheKey];
      const endpointName: string | undefined = sub?.endpointName;
      const originalArgs = sub?.originalArgs;

      const detail = {
        changed,
        patchesCount: patches.length,
        affectedItems: countAffectedItems(patches),
        ops: countOps(patches), // { add, remove, replace }
        endpointName,
        queryCacheKey,
        originalArgs,
        slice: matchedSlice?.reducerPath,
        ts: Date.now(),
      };

      // Spara senaste (undvik global typings-krockar)
      (window as any).__RTKQ_LAST_PATCH__ = detail;

      window.dispatchEvent(new CustomEvent(RTKQ_PATCH_EVENT, { detail }));
    }

    // ---- ERROR (mutation/query rejected) ----
    const isErrorAction = slices.some((api) =>
      [`${api.reducerPath}/executeMutation/rejected`, `${api.reducerPath}/executeQuery/rejected`].includes(action.type),
    );

    if (isErrorAction) {
      const matchedSlice = slices.find((api) => action.type.startsWith(`${api.reducerPath}/`));
      const endpointName: string | undefined = action?.meta?.arg?.endpointName;
      const originalArgs = action?.meta?.arg?.originalArgs;
      const error = action?.error ?? action?.payload;

      const detail = {
        endpointName,
        originalArgs,
        error,
        slice: matchedSlice?.reducerPath,
        ts: Date.now(),
      };

      window.dispatchEvent(new CustomEvent(RTKQ_PATCH_ERROR_EVENT, { detail }));
    }

    return res;
  };
